import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ExternalLink, Images, PencilLine, Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { GalleryEditor } from "@/components/admin/GalleryEditor";
import { api, errMsg } from "@/lib/api";
import { PAGES_QUERY, usePages } from "@/hooks/usePages";

const inp = "w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-2.5 text-cream outline-none focus:border-terra-soft placeholder:text-cream/40";
const iconBtn = "grid place-items-center h-9 w-9 rounded-full border border-cream/20 text-cream/80 hover:bg-cream hover:text-ink transition-colors disabled:opacity-30";

function usePageActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: PAGES_QUERY });
  const run = async (fn, ok) => {
    try {
      await fn();
      await refresh();
      if (ok) toast.success(ok);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return { run };
}

function AddPage() {
  const { run } = usePageActions();
  const [name, setName] = useState("");
  const [inMenu, setInMenu] = useState(true);
  const submit = (e) => {
    e.preventDefault();
    run(async () => { await api.post("/admin/pages", { name: name.trim(), in_menu: inMenu }); setName(""); }, "Page created. Click Edit content to fill it in.");
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-cream/10 bg-cream/5 p-5 flex flex-col sm:flex-row sm:items-end gap-4" data-testid="add-page-form">
      <label className="flex-1">
        <span className="eyebrow text-cream/70 block mb-2">New destination page</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={40} placeholder="e.g. Azerbaijan" className={inp} data-testid="add-page-name-input" />
      </label>
      <label className="flex items-center gap-2 text-sm text-cream/80 pb-2.5">
        <Switch checked={inMenu} onCheckedChange={setInMenu} data-testid="add-page-menu-switch" /> Show in top menu
      </label>
      <button className="inline-flex items-center justify-center gap-2 rounded-full bg-terra px-5 py-2.5 font-semibold text-paper hover:bg-terra-soft" data-testid="add-page-submit-btn">
        <Plus className="h-4 w-4" /> Create page
      </button>
    </form>
  );
}

function PageRow({ page, prev, next }) {
  const { run } = usePageActions();
  const [name, setName] = useState(page.name);
  const [showGallery, setShowGallery] = useState(false);
  const update = (body, ok) => run(() => api.put(`/admin/pages/${page.id}`, body), ok);
  const swap = (other) => run(async () => {
    await api.put(`/admin/pages/${page.id}`, { order: other.order });
    await api.put(`/admin/pages/${other.id}`, { order: page.order === other.order ? page.order + 1 : page.order });
  });
  const remove = () => {
    if (window.confirm(`Delete the "${page.name}" page and all its content?`)) run(() => api.delete(`/admin/pages/${page.id}`), "Page deleted");
  };
  const custom = page.kind === "destination";
  return (
    <li className="rounded-2xl border border-cream/10 bg-cream/5 p-4 grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center" data-testid={`page-row-${page.slug}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 min-w-0">
        <input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim().length >= 2 && name !== page.name && update({ name: name.trim() }, "Page renamed")}
          className={`${inp} sm:max-w-[16rem]`} data-testid={`page-name-input-${page.slug}`} aria-label="Menu name" />
        <span className="font-mono text-xs text-cream/60 truncate">{page.path}</span>
        <span className={`eyebrow rounded-full px-2.5 py-1 self-start sm:self-auto ${custom ? "bg-gold text-ink" : "bg-cream/10 text-cream/80"}`}>{custom ? "Your page" : "Built-in"}</span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-cream/80">
          <Switch checked={page.in_menu} onCheckedChange={(v) => update({ in_menu: v })} data-testid={`page-menu-switch-${page.slug}`} /> In menu
        </label>
        <label className="flex items-center gap-2 text-sm text-cream/80">
          <Switch checked={!page.hidden} onCheckedChange={(v) => update({ hidden: !v }, v ? "Page is visible" : "Page hidden from visitors")} data-testid={`page-visible-switch-${page.slug}`} /> Visible
        </label>
        <button onClick={() => swap(prev)} disabled={!prev} className={iconBtn} aria-label="Move up" data-testid={`page-up-${page.slug}`}><ArrowUp className="h-4 w-4" /></button>
        <button onClick={() => swap(next)} disabled={!next} className={iconBtn} aria-label="Move down" data-testid={`page-down-${page.slug}`}><ArrowDown className="h-4 w-4" /></button>
        <Link to={`${page.path}${page.path.includes("?") ? "&" : "?"}edit=1`} className="inline-flex items-center gap-1.5 rounded-full border border-cream/20 px-3.5 py-2 text-sm hover:bg-cream hover:text-ink transition-colors" data-testid={`page-edit-${page.slug}`}>
          <PencilLine className="h-4 w-4" /> Edit content
        </Link>
        <a href={page.path} target="_blank" rel="noreferrer" className={iconBtn} aria-label="Open page" data-testid={`page-open-${page.slug}`}><ExternalLink className="h-4 w-4" /></a>
        {custom && (
          <button onClick={() => setShowGallery((v) => !v)} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm transition-colors ${showGallery ? "bg-cream text-ink border-cream" : "border-cream/20 hover:bg-cream hover:text-ink"}`} data-testid={`page-gallery-toggle-${page.slug}`}>
            <Images className="h-4 w-4" /> Gallery ({page.gallery?.length ?? 0})
          </button>
        )}
        {custom && <button onClick={remove} className={`${iconBtn} hover:!bg-red-500 hover:!text-white`} aria-label="Delete page" data-testid={`page-delete-${page.slug}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
      {custom && showGallery && <GalleryEditor page={page} />}
    </li>
  );
}

export function PagesManager() {
  const { pages } = usePages();
  return (
    <div className="space-y-6" data-testid="pages-manager">
      <p className="text-cream/70 max-w-2xl">Add destination pages (same layout as the Georgia page), choose which pages appear in the top menu, change the order, or hide pages from visitors. Built-in pages can be hidden but not deleted.</p>
      <AddPage />
      <ul className="space-y-3">
        {pages.map((p, i) => <PageRow key={`${p.id}-${p.name}`} page={p} prev={pages[i - 1]} next={pages[i + 1]} />)}
      </ul>
    </div>
  );
}
